import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, MessageCircle, Mail, Link2, FileText, Trash2, Plus, X } from "lucide-react";
import { formatDateDE, toYMDLocal } from "@/lib/utils";
import { CustomerPicker } from "@/components/CustomerPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WD = ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"];
const weekday = (ymd) => {
  const [y, m, d] = (ymd || "").split("-").map(Number);
  if (!y) return "";
  return WD[new Date(y, m - 1, d).getDay()];
};

const EMPTY = { betrieb: "", kontakt_name: "", phone: "", email: "", ort: "", notiz: "" };

const statusBadge = (r) => {
  const base = "text-[9px] uppercase tracking-[0.15em] px-2 py-1 border";
  if (r.confirmed_at) return <span className={`${base} border-emerald-300 text-emerald-700 bg-emerald-50`}>bestätigt</span>;
  if (r.sent_at) return <span className={`${base} border-amber-300 text-amber-700 bg-amber-50`}>gesendet</span>;
  return <span className={`${base} border-[#E3DECE] text-[#757067]`}>offen</span>;
};

export default function EinsatzplanTab({ pwd }) {
  const [form, setForm] = useState(EMPTY);
  const [dates, setDates] = useState([]);
  const [newDate, setNewDate] = useState(toYMDLocal());
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/einsatzplaene`, { headers: { "X-Admin-Password": pwd } });
      setList(data || []);
    } catch {
      toast.error("Einsatzpläne konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, [pwd]);

  useEffect(() => { load(); }, [load]);

  const addDate = () => {
    if (!newDate) return;
    if (dates.includes(newDate)) { toast.error("Tag ist schon im Plan"); return; }
    setDates((d) => [...d, newDate].sort());
    const next = new Date(newDate);
    next.setDate(next.getDate() + 1);
    setNewDate(toYMDLocal(next));
  };

  const save = async () => {
    if (!form.betrieb.trim()) { toast.error("Bitte den Betrieb wählen"); return; }
    if (!dates.length) { toast.error("Bitte mindestens einen Einsatztag hinzufügen"); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/admin/einsatzplaene`, { ...form, dates },
        { headers: { "X-Admin-Password": pwd } });
      toast.success(`Einsatzplan ${data.plan_number} mit ${data.days_count} Tagen angelegt — jetzt per WhatsApp senden`);
      setForm(EMPTY);
      setDates([]);
      await load();
    } catch (err) {
      toast.error("Fehler: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const markSent = async (r, via) => {
    try {
      await axios.post(`${API}/admin/einsatzplaene/${r.id}/sent?via=${via}`, {},
        { headers: { "X-Admin-Password": pwd } });
      load();
    } catch { /* egal */ }
  };

  const sendWhatsApp = (r) => {
    window.open(r.whatsapp_url, "_blank", "noopener");
    markSent(r, "whatsapp");
  };

  const sendEmail = async (r) => {
    const to = r.email || window.prompt("E-Mail-Adresse des Betriebs:");
    if (!to) return;
    try {
      await axios.post(`${API}/admin/einsatzplaene/${r.id}/send-email`, { email: to },
        { headers: { "X-Admin-Password": pwd } });
      toast.success("Einsatzplan per E-Mail verschickt");
      load();
    } catch (err) {
      toast.error("Fehler: " + (err.response?.data?.detail || err.message));
    }
  };

  const copyLink = async (r) => {
    try {
      await navigator.clipboard.writeText(r.confirm_url);
      toast.success("Link kopiert");
    } catch {
      window.prompt("Link kopieren:", r.confirm_url);
    }
  };

  const openPdf = async (r) => {
    try {
      const res = await axios.get(`${API}/admin/einsatzplaene/${r.id}/pdf`, {
        headers: { "X-Admin-Password": pwd }, responseType: "blob",
      });
      window.open(URL.createObjectURL(res.data), "_blank", "noopener");
    } catch {
      toast.error("PDF konnte nicht geladen werden");
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Einsatzplan ${r.plan_number} (${r.betrieb}) wirklich löschen?`)) return;
    try {
      await axios.delete(`${API}/admin/einsatzplaene/${r.id}`, { headers: { "X-Admin-Password": pwd } });
      toast.success("Einsatzplan gelöscht");
      load();
    } catch (err) {
      toast.error("Fehler: " + (err.response?.data?.detail || err.message));
    }
  };

  return (
    <div data-testid="einsatzplan-tab" className="space-y-10">
      <div>
        <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52] font-semibold">Einsatzplan</p>
        <p className="text-sm text-[#757067] mt-1 max-w-2xl">
          Geplante Tage vorab bestätigen lassen — ohne Preise. Der Küchenchef bekommt per WhatsApp
          oder E-Mail einen Link, unterschreibt mit dem Finger, du bekommst das PDF als Sicherheit.
          Die Stunden erfasst du wie immer pro Einsatz, die Rechnung geht später als Sammelrechnung raus.
        </p>
      </div>

      <div className="border border-[#E3DECE] bg-[#FAFAF7] p-5 md:p-6">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52] mb-4">Neuer Einsatzplan</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#757067]">Betrieb *</label>
            <div className="mt-1">
              <CustomerPicker
                pwd={pwd}
                value={form.betrieb}
                onChange={(v) => set("betrieb", v)}
                onSelect={(k) => setForm((f) => ({
                  ...f,
                  betrieb: k.betrieb,
                  kontakt_name: k.ansprechpartner || f.kontakt_name,
                  phone: k.telefon || f.phone,
                  email: k.email || f.email,
                  ort: k.ort || f.ort,
                }))}
                placeholder="z. B. Hotel Zentlinde GbR"
                testId="plan-betrieb"
              />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#757067]">Küchenchef / Ansprechpartner</label>
            <input type="text" value={form.kontakt_name} onChange={(e) => set("kontakt_name", e.target.value)}
              data-testid="plan-kontakt" placeholder="Name"
              className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] px-3 py-3 text-sm" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#757067]">Handynummer (WhatsApp)</label>
            <input type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)}
              data-testid="plan-phone" placeholder="0151 2345678"
              className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] px-3 py-3 text-sm" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#757067]">E-Mail (optional)</label>
            <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
              data-testid="plan-email" placeholder="kueche@betrieb.de"
              className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] px-3 py-3 text-sm" />
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#757067]">Einsatzort (optional)</label>
            <input type="text" value={form.ort} onChange={(e) => set("ort", e.target.value)}
              data-testid="plan-ort" placeholder="Straße, PLZ Ort"
              className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] px-3 py-3 text-sm" />
          </div>
        </div>

        <div className="mt-6 border-t border-[#E3DECE] pt-5">
          <label className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">Einsatztage *</label>
          <div className="flex flex-wrap items-end gap-2 mt-2">
            <input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addDate(); } }}
              data-testid="plan-date-input"
              className="bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] px-3 py-2.5 text-sm" />
            <button type="button" onClick={addDate} data-testid="plan-date-add"
              className="border border-[#BA6D52] text-[#BA6D52] hover:bg-[#FAF5F1] px-4 py-2.5 text-xs uppercase tracking-[0.15em] inline-flex items-center gap-2">
              <Plus size={14} /> Tag hinzufügen
            </button>
            {dates.length > 0 && (
              <span className="text-xs text-[#757067]" data-testid="plan-date-count">{dates.length} Tage im Plan</span>
            )}
          </div>

          {dates.length > 0 && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="plan-date-list">
              {dates.map((d, i) => (
                <div key={d} className="flex items-center justify-between bg-white border border-[#E3DECE] px-3 py-2">
                  <span className="text-sm">
                    <span className="text-[#BA6D52] text-xs mr-2">{i + 1}.</span>
                    {formatDateDE(d)}
                    <span className="text-xs text-[#757067] ml-2">{weekday(d)}</span>
                  </span>
                  <button type="button" onClick={() => setDates((x) => x.filter((v) => v !== d))}
                    data-testid={`plan-date-remove-${d}`} title="Tag entfernen"
                    className="text-[#757067] hover:text-red-700">
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="mt-5">
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#757067]">Anmerkung an den Betrieb (optional)</label>
            <textarea value={form.notiz} onChange={(e) => set("notiz", e.target.value)} rows={2}
              data-testid="plan-notiz" placeholder="z. B. Posten Garde, Anreise jeweils 15:00"
              className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] px-3 py-3 text-sm" />
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
          <p className="text-xs text-[#757067] italic">
            Der Plan enthält bewusst keine Beträge — der Hinweis auf die Abrechnung nach erfassten Stunden steht automatisch drauf.
          </p>
          <button type="button" onClick={save} disabled={saving} data-testid="plan-save"
            className="bg-[#BA6D52] text-white hover:bg-[#A55C43] disabled:opacity-60 px-6 py-3 text-xs uppercase tracking-[0.15em] inline-flex items-center gap-2">
            {saving ? <Loader2 size={14} className="animate-spin" /> : null}
            Einsatzplan anlegen
          </button>
        </div>
      </div>

      <div>
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52] mb-3">Pläne</p>
        {loading ? (
          <Loader2 className="animate-spin text-[#BA6D52]" size={20} />
        ) : list.length === 0 ? (
          <p className="text-sm text-[#757067]" data-testid="plan-empty">Noch keine Einsatzpläne.</p>
        ) : (
          <div className="space-y-3">
            {list.map((r) => (
              <div key={r.id} data-testid={`plan-row-${r.id}`}
                className="border border-[#E3DECE] bg-white p-4 flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-[240px]">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-xs text-[#BA6D52]">{r.plan_number}</span>
                    {statusBadge(r)}
                  </div>
                  <p className="font-serif text-lg mt-1">{r.betrieb}</p>
                  <p className="text-xs text-[#757067] mt-1">
                    {r.days_count} Tage · {r.month_label}
                    {r.kontakt_name ? ` · ${r.kontakt_name}` : ""}
                  </p>
                  <p className="text-xs text-[#757067] mt-1">
                    {(r.dates || []).map((d) => formatDateDE(d)).join(" · ")}
                  </p>
                  {r.confirmed_at && (
                    <p className="text-[11px] text-emerald-700 mt-1">
                      bestätigt von {r.confirmed_by} am {new Date(r.confirmed_at).toLocaleDateString("de-DE")}
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {!r.confirmed_at && (
                    <>
                      <button type="button" onClick={() => sendWhatsApp(r)} data-testid={`plan-wa-${r.id}`}
                        className="text-[10px] uppercase tracking-[0.15em] border border-emerald-600 text-emerald-700 hover:bg-emerald-50 px-3 py-2 inline-flex items-center gap-2">
                        <MessageCircle size={13} /> WhatsApp
                      </button>
                      <button type="button" onClick={() => sendEmail(r)} data-testid={`plan-mail-${r.id}`}
                        className="text-[10px] uppercase tracking-[0.15em] border border-[#E3DECE] text-[#757067] hover:bg-[#FAFAF7] px-3 py-2 inline-flex items-center gap-2">
                        <Mail size={13} /> E-Mail
                      </button>
                      <button type="button" onClick={() => copyLink(r)} data-testid={`plan-link-${r.id}`}
                        className="text-[10px] uppercase tracking-[0.15em] border border-[#E3DECE] text-[#757067] hover:bg-[#FAFAF7] px-3 py-2 inline-flex items-center gap-2">
                        <Link2 size={13} /> Link
                      </button>
                    </>
                  )}
                  <button type="button" onClick={() => openPdf(r)} data-testid={`plan-pdf-${r.id}`}
                    className="text-[10px] uppercase tracking-[0.15em] border border-[#BA6D52] text-[#BA6D52] hover:bg-[#FAF5F1] px-3 py-2 inline-flex items-center gap-2">
                    <FileText size={13} /> PDF
                  </button>
                  <button type="button" onClick={() => remove(r)} data-testid={`plan-delete-${r.id}`}
                    className="text-[10px] uppercase tracking-[0.15em] border border-[#E3DECE] text-[#757067] hover:bg-red-50 hover:text-red-700 px-3 py-2">
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
